import React, { useId, useState } from "react";
import { AlertTriangle, ChevronDown, ExternalLink, Plus } from "lucide-react";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";

const REG_P_LINKS = [
  {
    label: "Limits on sharing (§ 1016.10)",
    href: "https://www.consumerfinance.gov/rules-policy/regulations/1016/10/#a-1",
  },
  {
    label: "Consent exception (§ 1016.15)",
    href: "https://www.consumerfinance.gov/rules-policy/regulations/1016/15/#a-1",
  },
];

interface Props {
  onAddConsentQuestion: () => void;
}

export function ConsentWarning({ onAddConsentQuestion }: Props) {
  const [showDetails, setShowDetails] = useState(false);
  const detailsId = useId();

  return (
    <Alert variant="warning" data-testid="consent-warning">
      <AlertTriangle />
      <Alert.Title className="font-semibold">Ask for permission to share reviews</Alert.Title>
      <Alert.Description className="gap-2">
        <p>
          This form has a rating question but no consent question, so its reviews are never approved to share. They
          can't be posted or shown where only approved reviews appear.
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-2">
          <Button type="button" size="sm" variant="outline" className="bg-white" onClick={onAddConsentQuestion}>
            <Plus className="mr-2 h-4 w-4" />
            Add consent question
          </Button>
          <button
            type="button"
            aria-expanded={showDetails}
            aria-controls={detailsId}
            onClick={() => setShowDetails(current => !current)}
            className="inline-flex items-center gap-1 text-xs font-medium underline-offset-2 hover:underline cursor-pointer"
          >
            Why consent matters
            <ChevronDown className={cn("size-3.5 transition-transform", showDetails && "rotate-180")} />
          </button>
        </div>
        {showDetails && (
          <div id={detailsId} className="mt-1 grid gap-2 border-t border-amber-200 pt-3">
            <p>
              A public review reveals that the reviewer is your client, which Regulation P treats as nonpublic personal
              information under the Gramm-Leach-Bliley Act. Sharing it is allowed with the client's consent, and some
              states, such as California and Vermont, require that consent. Check with your compliance team for what
              applies to you.
            </p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              <span className="font-medium">Regulation P:</span>
              {REG_P_LINKS.map(link => (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 whitespace-nowrap underline underline-offset-2 hover:no-underline"
                >
                  {link.label}
                  <ExternalLink className="size-3" />
                </a>
              ))}
            </div>
          </div>
        )}
      </Alert.Description>
    </Alert>
  );
}
